import { Redirect, Route, Switch, useParams } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { queryClient } from "@/lib/queryClient";
import { lazy, Suspense } from "react";
import { AuthProvider } from "@/contexts/AuthContext";
import { AdminAuthProvider } from "@/hooks/useAdminAuth";
import { Analytics } from "@vercel/analytics/react";

const SignIn = lazy(() => import("@/pages/SignIn"));
// WorkspaceDashboard removed — unified under AdminDashboard
// Settings removed — unified under AdminSettings
const Home = lazy(() => import("@/pages/Home"));
// SignUp removed — unified under Register
// SignUpSuccess removed — no longer used
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const RepresentativeLanding = lazy(() => import("@/pages/RepresentativeLanding"));
const CRMCapture = lazy(() => import("@/pages/CRMCapture"));
// AdminLogin removed — unified under SignIn
const Register = lazy(() => import("@/pages/Register"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminRepresentatives = lazy(() => import("@/pages/admin/Representatives"));
const AdminRepresentativeContacts = lazy(() => import("@/pages/admin/RepresentativeContacts"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AcceptInvitation = lazy(() => import("@/pages/admin/AcceptInvitation"));
const NotFound = lazy(() => import("@/pages/not-found"));

/** Redirects /crm/ssw and /crm/ufe (with optional :user) to /crm or /crm/:user so org is not in the URL. */
function LegacyCrmRedirect() {
  const params = useParams<{ user?: string }>();
  const user = params?.user;
  return <Redirect to={user ? `/crm/${user}` : "/crm"} />;
}

function Router() {
  return (
    <Suspense fallback={<div className="flex min-h-[50vh] items-center justify-center text-muted-foreground">Loading...</div>}>
      <Switch>
        {/* Canonical unified app routes */}
        <Route path="/dashboard" component={AdminDashboard} />
        <Route path="/contacts" component={AdminRepresentativeContacts} />
        <Route path="/my-contacts" component={AdminRepresentativeContacts} />
        <Route path="/contact-cards" component={AdminRepresentatives} />
        <Route path="/admin-settings" component={AdminSettings} />

        {/* Legacy customer workspace → canonical routes */}
        <Route path="/workspace">
          <Redirect to="/dashboard" />
        </Route>
        <Route path="/settings">
          <Redirect to="/admin-settings" />
        </Route>
        <Route path="/signin" component={SignIn} />
        <Route path="/login" component={SignIn} />
        <Route path="/signup">
          <Redirect to="/register" />
        </Route>
        <Route path="/signup-success">
          <Redirect to="/register" />
        </Route>
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password/:token" component={ResetPassword} />
        <Route path="/verify-email/:token" component={VerifyEmail} />
        <Route path="/rep/:slug" component={RepresentativeLanding} />
        {/* Legacy: /crm/ssw or /crm/ssw/:user -> /crm or /crm/:user (remove org from URL) */}
        <Route path="/crm/ssw/:user?">
          <LegacyCrmRedirect />
        </Route>
        <Route path="/crm/ufe/:user?">
          <LegacyCrmRedirect />
        </Route>
        <Route path="/crm/:user" component={CRMCapture} />
        <Route path="/crm" component={CRMCapture} />
        <Route path="/admin/login" component={SignIn} />
        <Route path="/register" component={Register} />
        <Route path="/invite/:token" component={AcceptInvitation} />
        <Route path="/admin/invite/:token" component={AcceptInvitation} />

        {/* Legacy admin routes -> canonical routes */}
        <Route path="/admin/dashboard">
          <Redirect to="/dashboard" />
        </Route>
        <Route path="/admin/contact-cards">
          <Redirect to="/contact-cards" />
        </Route>
        <Route path="/admin/representatives">
          <Redirect to="/contact-cards" />
        </Route>
        <Route path="/admin/contacts">
          <Redirect to="/contacts" />
        </Route>
        <Route path="/admin/representative-contacts">
          <Redirect to="/contacts" />
        </Route>
        <Route path="/admin/settings">
          <Redirect to="/admin-settings" />
        </Route>
        <Route path="/admin">
          <Redirect to="/dashboard" />
        </Route>
        <Route path="/" component={Home} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
          <AuthProvider>
            <AdminAuthProvider>
              <TooltipProvider>
                <div className="min-h-screen flex flex-col">
                  <main className="flex-grow">
                    <Router />
                  </main>
                  <Toaster />
                  <Analytics />
                </div>
              </TooltipProvider>
            </AdminAuthProvider>
          </AuthProvider>
        </ThemeProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}
